// ✅ SKILL FACTURXPRO: Architecture 3 couches
// Component (UI only) → Custom Hook (Logic) → API Layer (HTTP)
// ✅ MIGRATED TO V2 DESIGN SYSTEM - Sprint 3

import {
  Ban,
  Bell,
  Calendar,
  CalendarClock,
  CheckCircle,
  Clock,
  Download,
  Edit,
  EuroIcon,
  Eye,
  FileEdit,
  FileText,
  MoreVertical,
  Plus,
  Receipt,
  Send,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVatPeriods, getVatPeriodBadgeColor } from '../../tax';
import type { VatPeriod } from '../../tax';
import { confirm } from "../../../stores/confirmStore";
import { useInvoicesManagement } from "../../../hooks/useInvoicesManagement";

// ✅ V2 DESIGN SYSTEM IMPORTS
import {
  Button,
  Select,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  MobileCard,
  DropdownMenu,
  EmptyState,
  Spinner,
  type DropdownMenuItem,
} from '@/components/ui-v2';

import {
  FilterBar,
  FilterOption,
} from '@/components/ui';
import { PaymentSchedulesManagement } from '../../payments/components/PaymentSchedulesManagement';
import { downloadCSV, formatDateForExport, formatCurrencyForExport, type ExportColumn } from '@/utils/exportUtils';
import { BatchInvoiceImport } from './BatchInvoiceImport';

export const InvoicesListResponsive: React.FC = () => {
  // ✅ SKILL FACTURXPRO: Logique extraite vers custom hook
  const {
    // Data
    invoices,
    filteredInvoices,
    loading,
    // Filter State
    searchQuery,
    activeStatuses,
    dateFrom,
    dateTo,
    // Actions
    deleteInvoice,
    updateInvoiceStatus,
    markInvoiceAsPaid,
    sendReminderForInvoice,
    getStatusConfig,
    isOverdue,
    formatDate,
    formatCurrency,
    // UI Actions
    setActiveMenu,
    setSearchQuery,
    setActiveStatuses,
    setDateFrom,
    setDateTo,
  } = useInvoicesManagement();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showBatchImport, setShowBatchImport] = useState(false);

  // ✅ Sprint 5 - Liens Contextuels: Périodes TVA
  const { periods: vatPeriods } = useVatPeriods();

  // Helper pour trouver la période TVA correspondant à une date
  const findVatPeriodForDate = (date: string | Date | null): VatPeriod | null => {
    if (!date || vatPeriods.length === 0) return null;
    const targetDate = typeof date === 'string' ? new Date(date) : date;
    return vatPeriods.find(period => {
      const startDate = new Date(period.startDate);
      const endDate = new Date(period.endDate);
      return targetDate >= startDate && targetDate <= endDate;
    }) || null;
  };

  // ✅ CONFORMITÉ LÉGALE: Soft delete uniquement pour factures DRAFT
  // 🔒 Les factures envoyées/payées ne peuvent JAMAIS être supprimées (obligation légale)
  const handleDelete = async (id: string) => {
    const invoice = invoices.find(inv => inv.id === id);

    if (!invoice) return;

    // 🚫 Si la facture n'est PAS en DRAFT, afficher un message explicatif éducatif
    if (invoice.status !== 'DRAFT') {
      await confirm({
        title: t('invoices.list.cannotCancelTitle'),
        message: invoice.status === 'SENT' ? t('invoices.list.cannotCancelSent') : t('invoices.list.cannotCancelPaid'),
        confirmText: t('invoices.list.understood'),
        variant: "warning",
      });
      return;
    }

    // ✅ Si DRAFT, demander confirmation d'annulation
    const confirmed = await confirm({
      title: t('invoices.list.cancelDraftTitle'),
      message: t('invoices.list.cancelDraftMessage'),
      confirmText: t('invoices.list.cancelDraftConfirm'),
      cancelText: t('invoices.list.cancelDraftKeep'),
      variant: "warning",
    });
    if (!confirmed) return;

    await deleteInvoice(id);
  };

  const handleMarkAsPaid = async (invoice: any) => {
    const confirmed = await confirm({
      title: t('invoices.list.markAsPaidTitle'),
      message: t('invoices.list.markAsPaidMessage', { number: invoice.number }),
      confirmText: t('common.actions.confirm'),
      cancelText: t('common.actions.cancel'),
      variant: "info",
    });
    if (!confirmed) return;

    await markInvoiceAsPaid(invoice.id.toString(), invoice.totalTTC);
  };

  const handleSendReminder = async (invoice: any) => {
    const confirmed = await confirm({
      title: t('invoices.list.sendReminderTitle'),
      message: t('invoices.list.sendReminderMessage', { number: invoice.number, email: invoice.Client?.email || t('common.client') }),
      confirmText: t('common.actions.send'),
      cancelText: t('common.actions.cancel'),
      variant: "info",
    });
    if (!confirmed) return;

    await sendReminderForInvoice(invoice.id);
  };

  // ✅ State pour le filtre échéancier
  const [filterHasSchedule, setFilterHasSchedule] = useState<boolean | null>(null);

  // ✅ State pour la popup des échéanciers
  const [selectedInvoiceForSchedule, setSelectedInvoiceForSchedule] = useState<any | null>(null);

  // ✅ SKILL FACTURXPRO: Computed values spécifiques à l'UI (status counts pour les filtres)
  const statusCounts = useMemo(() => {
    return invoices.reduce((acc, invoice) => {
      const status = invoice.status || "DRAFT";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [invoices]);

  // ✅ Count invoices with payment schedules
  const schedulesCount = useMemo(() => {
    return invoices.filter((inv: any) => inv._count?.PaymentSchedule > 0).length;
  }, [invoices]);

  // ✅ Apply local filter for payment schedules
  const displayedInvoices = useMemo(() => {
    if (filterHasSchedule === null) return filteredInvoices;
    return filteredInvoices.filter((inv: any) => {
      const hasSchedule = (inv._count?.PaymentSchedule || 0) > 0;
      return filterHasSchedule ? hasSchedule : !hasSchedule;
    });
  }, [filteredInvoices, filterHasSchedule]);

  // Options de filtres par statut
  const statusFilterOptions: FilterOption[] = [
    {
      value: "DRAFT",
      label: t('invoices.status.DRAFT'),
      icon: FileEdit,
      color: "gray",
      count: statusCounts.DRAFT || 0,
    },
    {
      value: "SENT",
      label: t('invoices.status.SENT'),
      icon: Send,
      color: "blue",
      count: statusCounts.SENT || 0,
    },
    {
      value: "PAID",
      label: t('invoices.status.PAID'),
      icon: CheckCircle,
      color: "green",
      count: statusCounts.PAID || 0,
    },
    {
      value: "OVERDUE",
      label: t('invoices.status.OVERDUE'),
      icon: XCircle,
      color: "red",
      count: statusCounts.OVERDUE || 0,
    },
    {
      value: "CANCELED",
      label: t('invoices.status.CANCELED'),
      icon: Ban,
      color: "gray",
      count: statusCounts.CANCELED || 0,
    },
  ];

  // Handlers pour les filtres
  const handleStatusToggle = (status: string) => {
    const newStatuses = activeStatuses.includes(status)
      ? activeStatuses.filter((s) => s !== status)
      : [...activeStatuses, status];
    setActiveStatuses(newStatuses);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setActiveStatuses([]);
    setDateFrom("");
    setDateTo("");
    setFilterHasSchedule(null);
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      DRAFT: t('invoices.status.DRAFT'),
      SENT: t('invoices.status.SENT'),
      PAID: t('invoices.status.PAID'),
      OVERDUE: t('invoices.status.OVERDUE'),
      CANCELED: t('invoices.status.CANCELED'),
    };
    return labels[status] || status;
  };

  // Helper pour obtenir l'icône selon le statut
  const getStatusIcon = (status: string) => {
    const statusMap: Record<string, any> = {
      DRAFT: FileEdit,
      SENT: Send,
      PAID: CheckCircle,
      OVERDUE: XCircle,
      CANCELED: Ban,
    };
    return statusMap[status] || FileEdit;
  };

  // Options de statut disponibles pour le Select
  const getStatusOptions = () => {
    return [
      { value: 'DRAFT', label: t('invoices.status.DRAFT') },
      { value: 'SENT', label: t('invoices.status.SENT') },
      { value: 'PAID', label: t('invoices.status.PAID') },
      { value: 'OVERDUE', label: t('invoices.status.OVERDUE') },
      { value: 'CANCELED', label: t('invoices.status.CANCELED') },
    ];
  };

  // ✅ Sprint 4 - Export CSV
  const handleExportCSV = () => {
    const columns: ExportColumn<typeof displayedInvoices[0]>[] = [
      { key: 'number', header: t('invoices.list.tableNumber') },
      {
        key: 'Client.companyName',
        header: t('invoices.list.tableClient'),
        formatter: (_, row) => row.Client?.companyName || row.Client?.fullName || '',
      },
      {
        key: 'status',
        header: t('invoices.list.tableStatus'),
        formatter: (value) => getStatusLabel(value as string),
      },
      {
        key: 'issueDate',
        header: t('invoices.list.tableDate'),
        formatter: (value) => formatDateForExport(value as string),
      },
      {
        key: 'dueDate',
        header: t('invoices.list.tableDueDate'),
        formatter: (value) => formatDateForExport(value as string),
      },
      {
        key: 'totalHT',
        header: t('invoices.totalHT'),
        formatter: (value) => formatCurrencyForExport(value as number),
      },
      {
        key: 'totalTVA',
        header: t('invoices.tax'),
        formatter: (value) => formatCurrencyForExport(value as number),
      },
      {
        key: 'totalTTC',
        header: t('invoices.totalTTC'),
        formatter: (value) => formatCurrencyForExport(value as number),
      },
      {
        key: 'paymentDate',
        header: t('invoices.issueDate'),
        formatter: (value) => value ? formatDateForExport(value as string) : '',
      },
    ];

    const today = new Date().toISOString().split('T')[0];
    downloadCSV(displayedInvoices, columns, {
      filename: `factures_export_${today}`,
      format: 'csv',
    });
  };

  // Batch Import View
  if (showBatchImport) {
    return <BatchInvoiceImport onClose={() => setShowBatchImport(false)} />;
  }

  return (
    <div className="px-8 pt-8 pb-12 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900">
            {t('invoices.title')}
          </h2>
          <p className="text-base text-gray-500 font-light tracking-wide mt-2">
            {filteredInvoices.length === invoices.length
              ? t('invoices.list.totalCount', { count: invoices.length })
              : t('invoices.list.filteredCount', { filtered: filteredInvoices.length, total: invoices.length })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Export CSV Button */}
          {displayedInvoices.length > 0 && (
            <Button
              variant="outline"
              onClick={handleExportCSV}
              icon={<Download className="w-5 h-5" />}
            >
              {t('common.exportCSV')}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setShowBatchImport(true)}
            icon={<Upload className="w-5 h-5" />}
          >
            {t('invoices.batchImport.button', 'Importer (PDF)')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate("/invoices/new")}
            icon={<Plus className="w-5 h-5" />}
          >
            {t('invoices.create')}
          </Button>
        </div>
      </div>

      {/* Filter Bar - Always visible when not loading */}
      {!loading && (
        <FilterBar
          searchPlaceholder={t('common.searchByNumberClientAmount')}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          statusFilters={statusFilterOptions}
          activeStatuses={activeStatuses}
          onStatusToggle={handleStatusToggle}
          showDateFilter={true}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onClearFilters={handleClearFilters}
          totalItems={invoices.length}
          filteredItems={displayedInvoices.length}
          customFilters={schedulesCount > 0 ? [
            {
              id: 'has-schedule',
              label: t('invoices.list.withSchedule'),
              icon: CalendarClock,
              count: schedulesCount,
              active: filterHasSchedule === true,
              onClick: () => setFilterHasSchedule(filterHasSchedule === true ? null : true),
            }
          ] : []}
        />
      )}

      {/* Main Content Container */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 overflow-hidden">
        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <Spinner size="lg" />
            <p className="mt-4 text-gray-600">{t('invoices.list.loading')}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && invoices.length === 0 && (
          <div className="p-8">
            <EmptyState
              icon={<FileText className="w-12 h-12" />}
              title={t('invoices.list.empty')}
              description={t('invoices.list.emptyDescription')}
            />
          </div>
        )}

        {/* No results after filtering */}
        {!loading && invoices.length > 0 && displayedInvoices.length === 0 && (
          <div className="p-8">
            <EmptyState
              icon={<FileText className="w-12 h-12" />}
              title={t('invoices.list.noResults')}
              description={t('invoices.list.emptyFilter')}
              ctaLabel={t('common.clearFilters')}
              onCtaClick={handleClearFilters}
            />
          </div>
        )}

        {/* Invoices Grid - Mobile Cards / Desktop Table */}
        {!loading && displayedInvoices.length > 0 && (
          <>
          {/* Mobile Card View */}
          <div className="grid grid-cols-1 gap-4 md:hidden p-6">
            {displayedInvoices.map((invoice: any) => {
              const hasSchedule = (invoice._count?.PaymentSchedule || 0) > 0;
              const statusConfig = getStatusConfig(invoice.status || "draft");
              const StatusIcon = statusConfig.icon;
              const overdueStatus = isOverdue(invoice);
              const vatPeriod = findVatPeriodForDate(invoice.issueDate);

              // Dropdown menu items
              const menuItems: DropdownMenuItem[] = [
                {
                  label: t('invoices.list.view'),
                  icon: <Eye className="w-4 h-4" />,
                  onClick: () => {
                    navigate(`/invoices/${invoice.id}`);
                    setActiveMenu(null);
                  },
                },
                {
                  label: t('invoices.list.modify'),
                  icon: <Edit className="w-4 h-4" />,
                  onClick: () => {
                    navigate(`/invoices/${invoice.id}/edit`);
                    setActiveMenu(null);
                  },
                },
              ];

              if (invoice.status !== "PAID") {
                menuItems.push({
                  label: t('invoices.list.markPaid'),
                  icon: <CheckCircle className="w-4 h-4" />,
                  onClick: () => {
                    handleMarkAsPaid(invoice);
                    setActiveMenu(null);
                  },
                });
              }

              menuItems.push({
                label: t('invoices.list.sendReminder'),
                icon: <Bell className="w-4 h-4" />,
                onClick: () => {
                  handleSendReminder(invoice);
                  setActiveMenu(null);
                },
              });

              if (hasSchedule) {
                menuItems.push({
                  label: t('invoices.list.schedule'),
                  icon: <CalendarClock className="w-4 h-4" />,
                  onClick: () => {
                    setSelectedInvoiceForSchedule(invoice);
                    setActiveMenu(null);
                  },
                });
              }

              menuItems.push({
                label: t('common.actions.delete'),
                icon: <Trash2 className="w-4 h-4" />,
                variant: 'danger',
                onClick: () => {
                  handleDelete(invoice.id);
                  setActiveMenu(null);
                },
              });

              return (
                <MobileCard
                  key={invoice.id}
                  header={
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
                          {invoice.number}
                          {hasSchedule && (
                            <span title={t('invoices.list.paymentSchedule')}>
                              <CalendarClock className="w-4 h-4 text-teal-600" aria-label={t('invoices.list.paymentSchedule')} />
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-600 truncate">
                          {invoice.Client?.fullName || invoice.Client?.companyName || "-"}
                        </p>
                        {invoice.Client?.companyName && invoice.Client?.fullName && (
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {invoice.Client?.companyName}
                          </p>
                        )}
                      </div>
                      <DropdownMenu
                        trigger={
                          <button className="p-2 hover:bg-white/30 rounded-lg transition-all">
                            <MoreVertical className="w-5 h-5 text-gray-600" />
                          </button>
                        }
                        items={menuItems}
                        align="right"
                      />
                    </div>
                  }
                  onClick={() => navigate(`/invoices/${invoice.id}`)}
                  className={overdueStatus ? "border-red-300 bg-red-50/10" : ""}
                >
                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">{t('common.issuedOn')}</p>
                        <p className="font-medium text-gray-900">
                          {formatDate(invoice.issueDate)}
                        </p>
                      </div>
                    </div>
                    {invoice.dueDate && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-500">{t('common.dueOn')}</p>
                          <p
                            className={`font-medium ${
                              overdueStatus ? "text-red-600" : "text-gray-900"
                            }`}
                          >
                            {formatDate(invoice.dueDate)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Amount */}
                  <div className="flex items-center justify-between py-2 border-t border-gray-100 mb-3">
                    <div className="flex items-center gap-2">
                      <EuroIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{t('invoices.totalTTC')}</span>
                    </div>
                    <span className="text-xl font-bold text-gray-900">
                      {formatCurrency(invoice.totalTTC)}
                    </span>
                  </div>

                  {/* Status Select */}
                  <Select
                    value={invoice.status || "DRAFT"}
                    onChange={(e) => updateInvoiceStatus(invoice.id, e.target.value)}
                    size="sm"
                    variant="chip"
                    fullWidth={true}
                    icon={getStatusIcon(invoice.status || "DRAFT")}
                  >
                    {getStatusOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>

                  {/* Overdue Alert */}
                  {overdueStatus && (
                    <div className="flex items-center gap-2 p-2 bg-red-100 rounded-lg mt-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      <span className="text-sm font-medium text-red-700">
                        {t('invoices.list.overdueAlert')}
                      </span>
                    </div>
                  )}

                  {/* VAT Period Badge - Sprint 5 */}
                  {vatPeriod && (
                    <div
                      className={`flex items-center gap-2 p-2 rounded-lg mt-2 cursor-pointer hover:opacity-80 transition-opacity ${getVatPeriodBadgeColor(vatPeriod)}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/tva');
                      }}
                      title={t('invoices.list.viewVatPeriod')}
                    >
                      <Receipt className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {t('invoices.list.vatPeriodLabel', { name: vatPeriod.name })}
                      </span>
                    </div>
                  )}
                </MobileCard>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell header>{t('invoices.list.tableNumber')}</TableCell>
                  <TableCell header>{t('invoices.list.tableClient')}</TableCell>
                  <TableCell header>{t('invoices.list.tableDate')}</TableCell>
                  <TableCell header>{t('invoices.list.tableDueDate')}</TableCell>
                  <TableCell header align="right">{t('invoices.list.tableAmountTTC')}</TableCell>
                  <TableCell header>{t('invoices.list.tableStatus')}</TableCell>
                  <TableCell header align="right">{t('invoices.list.tableActions')}</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedInvoices.map((invoice: any) => {
                  const overdueStatus = isOverdue(invoice);
                  const hasSchedule = (invoice._count?.PaymentSchedule || 0) > 0;
                  const vatPeriod = findVatPeriodForDate(invoice.issueDate);

                  return (
                    <TableRow
                      key={invoice.id}
                      hover
                      className={
                        overdueStatus
                          ? "bg-red-50/30"
                          : invoice.status === "PAID"
                          ? "bg-green-50/30"
                          : ""
                      }
                    >
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {invoice.number}
                            </span>
                            {overdueStatus && (
                              <XCircle className="w-4 h-4 text-red-600" />
                            )}
                          </div>
                          {/* VAT Period Badge - Sprint 5 */}
                          {vatPeriod && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/tva');
                              }}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium hover:opacity-80 transition-opacity ${getVatPeriodBadgeColor(vatPeriod)}`}
                              title={t('invoices.list.viewVatPeriod')}
                            >
                              <Receipt className="w-3 h-3" />
                              {vatPeriod.name}
                            </button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {invoice.Client?.fullName || invoice.Client?.companyName || "-"}
                          </div>
                          {invoice.Client?.companyName && invoice.Client?.fullName && (
                            <div className="text-sm text-gray-500">
                              {invoice.Client?.companyName}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDate(invoice.issueDate)}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`text-sm ${
                            overdueStatus
                              ? "font-semibold text-red-600"
                              : "text-gray-500"
                          }`}
                        >
                          {formatDate(invoice.dueDate)}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span
                          className={`text-sm font-semibold ${
                            invoice.status === "PAID"
                              ? "text-green-600"
                              : "text-gray-900"
                          }`}
                        >
                          {formatCurrency(invoice.totalTTC)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={invoice.status || "DRAFT"}
                          onChange={(e) => updateInvoiceStatus(invoice.id, e.target.value)}
                          size="sm"
                          variant="chip"
                          fullWidth={false}
                          icon={getStatusIcon(invoice.status || "DRAFT")}
                        >
                          {getStatusOptions().map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell align="right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/invoices/${invoice.id}`)}
                            className="p-2 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors"
                            title={t('invoices.list.view')}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/invoices/${invoice.id}/edit`)
                            }
                            className="p-2 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors"
                            title={t('invoices.list.modify')}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          {invoice.status !== "PAID" && (
                            <button
                              onClick={() => handleMarkAsPaid(invoice)}
                              className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors"
                              title={t('invoices.list.markPaid')}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleSendReminder(invoice)}
                            className="p-2 text-orange-600 hover:text-orange-900 hover:bg-orange-50 rounded-lg transition-colors"
                            title={t('invoices.list.sendReminder')}
                          >
                            <Bell className="w-4 h-4" />
                          </button>
                          {hasSchedule && (
                            <button
                              onClick={() => setSelectedInvoiceForSchedule(invoice)}
                              className="p-2 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors"
                              title={t('invoices.list.paymentSchedule')}
                            >
                              <CalendarClock className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(invoice.id)}
                            className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                            title={t('common.actions.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          </>
        )}
      </div>

      {/* Payment Schedules Modal */}
      {selectedInvoiceForSchedule && (
        <PaymentSchedulesManagement
          invoiceId={selectedInvoiceForSchedule.id}
          invoiceNumber={selectedInvoiceForSchedule.number}
          invoiceTotal={Number(selectedInvoiceForSchedule.totalTTC) || 0}
          onClose={() => setSelectedInvoiceForSchedule(null)}
        />
      )}
    </div>
  );
};
